import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Award, 
  DollarSign, 
  AlertTriangle, 
  Save, 
  RefreshCw, 
  Trash2,
  TrendingUp,
  Users,
  Calculator,
  Shield,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import UsersService from './home/homeservice';

const SettingsConfiguration = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Configuration state
  const [config, setConfig] = useState({
    points_multiplier: 1.0,
    points_to_tsh_rate: 10.0,
    is_points_system_active: true,
    total_active_points: 0,
    last_reset_date: null
  });

  // Form state
  const [formData, setFormData] = useState({
    points_multiplier: 1.0,
    points_to_tsh_rate: 10.0,
    is_points_system_active: true
  });

  // Reset confirmation state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');

  // Load configuration from API
  const loadConfiguration = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Loading configuration...');
      const response = await UsersService.getRewardPointsConfig();
      
      setConfig({
        points_multiplier: response.points_multiplier || 1.0,
        points_to_tsh_rate: response.points_to_tsh_rate || 10.0,
        is_points_system_active: response.is_points_system_active !== undefined ? response.is_points_system_active : true,
        total_active_points: response.total_active_points || 0,
        last_reset_date: response.last_reset_date
      });

      // Update form data to match loaded config
      setFormData({
        points_multiplier: response.points_multiplier || 1.0,
        points_to_tsh_rate: response.points_to_tsh_rate || 10.0,
        is_points_system_active: response.is_points_system_active !== undefined ? response.is_points_system_active : true
      });
      
      console.log('Configuration loaded successfully');
      
    } catch (error) {
      console.error('Error loading configuration:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          error.message || 
                          error.detail || 
                          'Failed to load configuration. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Save configuration
  const handleSaveConfiguration = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Saving configuration:', formData);

      // Validate form data
      if (formData.points_multiplier < 0) {
        throw new Error('Points multiplier cannot be negative');
      }
      if (formData.points_to_tsh_rate <= 0) {
        throw new Error('TZS rate must be greater than 0');
      }

      const response = await UsersService.updateRewardPointsConfig(formData);
      
      // Update local config with saved data
      setConfig(prev => ({
        ...prev,
        ...formData
      }));
      
      setSuccess(response.message || 'Configuration updated successfully!');
      console.log('Configuration saved successfully');
      
    } catch (error) {
      console.error('Error saving configuration:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          error.message || 
                          error.detail || 
                          'Failed to save configuration';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Reset reward points
  const handleResetRewardPoints = async () => {
    if (resetConfirmation !== 'RESET ALL POINTS') {
      setError('Please type the confirmation text exactly as shown');
      return;
    }

    setResetting(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Resetting all reward points...');
      
      const response = await UsersService.resetAllRewardPoints({
        confirm: true,
        confirm_all_users: true,
        reset_all: true
      });
      
      // Update local state to reflect reset
      setConfig(prev => ({
        ...prev,
        total_active_points: 0,
        last_reset_date: new Date().toISOString()
      }));
      
      setShowResetModal(false);
      setResetConfirmation('');
      setSuccess(response.message || `Reset completed successfully! ${response.details?.users_affected || 'All'} users affected.`);
      
      console.log('Reward points reset successfully');
      
    } catch (error) {
      console.error('Error resetting reward points:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          error.message || 
                          error.detail || 
                          'Failed to reset reward points';
      setError(errorMessage);
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const calculateTotalValue = () => {
    return (config.total_active_points || 0) * (formData.points_to_tsh_rate || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-8xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Settings className="h-8 w-8 text-orange-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">System Configuration</h1>
              <p className="text-gray-600">Manage reward points and system settings</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-6 py-6">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-green-800">{success}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center">
              <XCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Statistics */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-600" />
                Current Statistics
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-900">Total Active Points</span>
                  </div>
                  <span className="text-lg font-bold text-orange-900">
                    {(config.total_active_points || 0).toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-900">Total Value (TZS)</span>
                  </div>
                  <span className="text-lg font-bold text-green-900">
                    {formatCurrency(calculateTotalValue())}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Calculator className="h-4 w-4 text-white" />
                    <span className="text-sm font-medium text-white">Current Rate</span>
                  </div>
                  <span className="text-sm font-bold text-white">
                    1 point = {formatCurrency(config.points_to_tsh_rate || 0)}
                  </span>
                </div>

                {config.last_reset_date && (
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-900">Last Reset</span>
                    </div>
                    <span className="text-xs text-orange-900">
                      {new Date(config.last_reset_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={loadConfiguration}
                className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh Data
              </button>
            </div>
          </div>

          {/* Configuration Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Points Configuration */}
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-600" />
                Reward Points Configuration
              </h2>

              <form onSubmit={handleSaveConfiguration} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Points Multiplier
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.points_multiplier}
                      onChange={(e) => setFormData({
                        ...formData,
                        points_multiplier: parseFloat(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="1.0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Multiplier for points earned (e.g., 1.5 = 50% bonus)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Value per Point (TZS)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.points_to_tsh_rate}
                      onChange={(e) => setFormData({
                        ...formData,
                        points_to_tsh_rate: parseFloat(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="10.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      TZS value when converting points to money
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="points_active"
                    checked={formData.is_points_system_active}
                    onChange={(e) => setFormData({
                      ...formData,
                      is_points_system_active: e.target.checked
                    })}
                    className="h-4 w-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                  />
                  <label htmlFor="points_active" className="text-sm font-medium text-gray-700">
                    Reward Points System Active
                  </label>
                </div>

                {/* Preview Calculation */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-orange-800 mb-2">Preview Calculation</h4>
                  <div className="text-sm text-orange-700">
                    <p>With current settings:</p>
                    <p>• 100 points = {formatCurrency(100 * formData.points_to_tsh_rate)}</p>
                    <p>• 1,000 points = {formatCurrency(1000 * formData.points_to_tsh_rate)}</p>
                    <p>• Multiplier effect: Base points × {formData.points_multiplier}</p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Configuration
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Dangerous Actions */}
            <div className="bg-white rounded-lg border border-red-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Dangerous Actions
              </h2>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800 mb-1">Reset All Reward Points</h4>
                    <p className="text-sm text-red-700">
                      This will reset ALL user reward points to 0.00. This action cannot be undone.
                      Current total: <strong>{(config.total_active_points || 0).toLocaleString()} points</strong>
                      (Worth: <strong>{formatCurrency(calculateTotalValue())}</strong>)
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowResetModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Reset All Reward Points
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-red-900 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Confirm Reset All Reward Points
              </h3>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 mb-3">
                  You are about to reset <strong>ALL</strong> user reward points to 0.00.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-800">
                    • Total points to be reset: <strong>{(config.total_active_points || 0).toLocaleString()}</strong>
                  </p>
                  <p className="text-sm text-red-800">
                    • Monetary value: <strong>{formatCurrency(calculateTotalValue())}</strong>
                  </p>
                  <p className="text-sm text-red-800">
                    • This action is <strong>irreversible</strong>
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type "RESET ALL POINTS" to confirm:
                </label>
                <input
                  type="text"
                  value={resetConfirmation}
                  onChange={(e) => setResetConfirmation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="RESET ALL POINTS"
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setResetConfirmation('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={resetting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetRewardPoints}
                  disabled={resetConfirmation !== 'RESET ALL POINTS' || resetting}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {resetting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Resetting...
                    </>
                  ) : (
                    'Reset All Points'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsConfiguration;